/**
 * Story 9.28: gemeinsame Trefferliste der Artikelsuche in „Neuer Auftrag“, „Neues Angebot“ und der Bearbeiten-Maske –
 * je Treffer Nummer, Name, Bestand-Ampel (immer Punkt + Text), Lagerplatz und Netto-Einzelpreis. Alle Angaben kommen in
 * EINER Anfrage je Trefferliste vom Server (`POST /api/v1/manual-orders/article-facts`); der Browser rechnet keinen Preis
 * und keine Ampel selbst (keine zweite Preislogik). Ohne Kunde steht „Listenpreis“ vor dem Preis.
 *
 * Preise: Abnahme durch Steuerberater/Rudolf nötig.
 */
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { apiFetch } from '../lib/api.ts';
import { formatUnitPrice } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';

export const ARTICLE_FACTS_PATH = '/api/v1/manual-orders/article-facts';

/** Höchstens so viele Zeilen je Anfrage (Server: `ARTICLE_FACTS_MAX_LINES` in orders). */
export const ARTICLE_FACTS_MAX_LINES = 200;

export const ARTICLE_HITS_TEXTS = {
  listPrice: 'Listenpreis',
  net: 'netto',
  noPrice: 'kein Preis',
  bin: (code: string) => `Lagerplatz ${code}`,
  factsFailed: 'Bestand und Preis konnten nicht geladen werden.',
  retry: 'Erneut laden',
} as const;

export type StockLightTone = 'gruen' | 'gelb' | 'rot' | 'neutral';

export interface ArticleFactsLine {
  index: number;
  productNumber: string;
  available: number;
  missing: number;
  binCode: string | null;
  /** Story 1.265: Packeinheit des Artikels; `null` = nicht gepflegt (Anzeige „Stück“). */
  packUnit: string | null;
  packUnitPlural: string | null;
  light: 'gruen' | 'gelb' | 'rot';
  lightText: string;
  unitPrice: string | null;
  currency: string;
  priceKind: 'customer' | 'list';
}

export interface ArticleFactsResponse {
  lines: ArticleFactsLine[];
  threshold: number;
  thresholdVersion: number;
  version: string;
}

export interface ArticleFactsRequest {
  lines: { productNumber: string; quantity: number }[];
  customerId: string | null;
  currency: string | null;
  orderId: string | null;
  prices: boolean;
}

/**
 * Was von der vorherigen Antwort stehen bleiben darf, solange die neue lädt: nur bei gleichem Kunden, gleicher Währung,
 * gleichem Auftrag und gleicher Preisfrage, und je Zeile nur, wenn alle Zeilen bis einschließlich dieser (Nummer und
 * Menge) unverändert sind – sonst stünde kurz ein alter Preis oder eine alte Ampel da (Review OpenAI zu #1023).
 */
export function keepPreviousFacts(previous: ArticleFactsResponse | undefined, previousRequest: ArticleFactsRequest | null | undefined, request: ArticleFactsRequest | null): ArticleFactsResponse | undefined {
  if (!previous || !previousRequest || !request) return undefined;
  if (previousRequest.customerId !== request.customerId || previousRequest.currency !== request.currency || previousRequest.orderId !== request.orderId || previousRequest.prices !== request.prices) return undefined;
  let same = 0;
  while (same < request.lines.length && same < previousRequest.lines.length && request.lines[same]!.productNumber === previousRequest.lines[same]!.productNumber && request.lines[same]!.quantity === previousRequest.lines[same]!.quantity) same++;
  const lines = previous.lines.filter((l) => l.index < same);
  return lines.length === 0 ? undefined : { ...previous, lines };
}

/** Eine Anfrage je Treffer- bzw. Positionsliste; `null` = nichts abfragen. */
export function useArticleFacts(request: ArticleFactsRequest | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['article-facts', request],
    enabled: request !== null && request.lines.length > 0,
    staleTime: 5_000,
    placeholderData: (previous, previousQuery) => keepPreviousFacts(previous, previousQuery?.queryKey[1] as ArticleFactsRequest | null | undefined, request),
    queryFn: () => apiFetch<ArticleFactsResponse>(getToken, ARTICLE_FACTS_PATH, { method: 'POST', body: request! }),
  });
}

/** Ampel nach DESIGN: immer Punkt UND Text, nur Status-Farben (grün/gelb/rot, neutral grau). */
export function StockLightMark({ tone, text, testId, className }: { tone: StockLightTone; text: string; testId?: string; className?: string }) {
  return (
    <span className={cn('type-meta inline-flex items-center gap-space-2 tabular-nums', className)} data-testid={testId} data-ampel={tone}>
      <span aria-hidden className={cn('size-2 flex-none rounded-full', tone === 'rot' ? 'bg-clarify' : tone === 'gelb' ? 'bg-check-dot' : tone === 'gruen' ? 'bg-signal-green' : 'bg-ink-3')} />
      <span className={tone === 'rot' ? 'text-clarify' : tone === 'gelb' ? 'text-check' : 'text-ink-2'}>{text}</span>
    </span>
  );
}

/** Preistext eines Treffers: „12,40 € netto“, ohne Kunde „Listenpreis 12,40 € netto“, sonst „kein Preis“. */
export function hitPriceText(line: Pick<ArticleFactsLine, 'unitPrice' | 'currency' | 'priceKind'>): string {
  if (line.unitPrice === null) return ARTICLE_HITS_TEXTS.noPrice;
  const price = `${formatUnitPrice(line.unitPrice, line.currency)} ${ARTICLE_HITS_TEXTS.net}`;
  return line.priceKind === 'list' ? `${ARTICLE_HITS_TEXTS.listPrice} ${price}` : price;
}

export interface ArticleHit {
  id: string;
  /** Artikelnummer. */
  title: string;
  /** Name. */
  subtitle: string | null;
}

/**
 * Treffer als `<li>` (für ein vorhandenes `<ul>`); genau ein Knopf je Treffer, Klick übernimmt wie bisher. Mindestens
 * 44 px hoch, bricht bei 375 px um.
 */
export function ArticleHitItems({
  hits,
  customerId,
  currency,
  orderId,
  onTake,
}: {
  hits: readonly ArticleHit[];
  customerId: string | null;
  currency: string | null;
  /** Bearbeiten-Maske: Auftrag (eigene Reservierungen zählen als frei, Kunde/Währung vom Auftrag). */
  orderId: string | null;
  onTake: (hit: ArticleHit) => void;
}): ReactNode {
  const facts = useArticleFacts(
    hits.length === 0 ? null : { lines: hits.map((h) => ({ productNumber: h.title, quantity: 1 })), customerId: orderId ? null : customerId || null, currency: orderId ? null : currency || null, orderId, prices: true },
  );
  const byNumber = new Map((facts.data?.lines ?? []).map((l) => [l.productNumber, l]));
  // Fehler nie still: sonst sähe die Liste aus wie Treffer ohne Bestand und Preis (Review OpenAI zu #1023).
  const failed =
    facts.isError && hits.length > 0 ? (
      <li key="fakten-fehler" role="alert" className="type-meta flex min-h-11 flex-wrap items-center gap-x-space-3 px-space-3 py-space-1 text-clarify" data-testid="treffer-fakten-fehler">
        <span>{ARTICLE_HITS_TEXTS.factsFailed}</span>
        <button type="button" onClick={() => void facts.refetch()} className="min-h-11 cursor-pointer font-medium underline">
          {ARTICLE_HITS_TEXTS.retry}
        </button>
      </li>
    ) : null;
  const items = hits.map((h) => {
    const f = byNumber.get(h.title) ?? null;
    return (
      <li key={h.id}>
        <button
          type="button"
          onClick={() => onTake(h)}
          data-testid={`treffer-${h.title}`}
          className="flex min-h-11 w-full cursor-pointer flex-wrap items-center gap-x-space-3 gap-y-0 rounded-field px-space-3 py-space-1 text-left text-row text-ink hover:bg-tile"
        >
          <span className="font-medium [overflow-wrap:anywhere]">{h.title}</span>
          <span className="type-meta min-w-0 text-ink-3 [overflow-wrap:anywhere]">{h.subtitle}</span>
          {f ? (
            <span className="flex min-w-0 flex-wrap items-center gap-x-space-3">
              <StockLightMark tone={f.light} text={f.lightText} testId={`treffer-ampel-${h.title}`} />
              {f.binCode ? (
                <span className="type-meta text-ink-3" data-testid={`treffer-lagerplatz-${h.title}`}>
                  {ARTICLE_HITS_TEXTS.bin(f.binCode)}
                </span>
              ) : null}
              <span className="type-meta text-ink-2 tabular-nums" data-testid={`treffer-preis-${h.title}`}>
                {hitPriceText(f)}
              </span>
            </span>
          ) : null}
        </button>
      </li>
    );
  });
  return failed ? [failed, ...items] : items;
}
